import { User, MapPin, Bell, HelpCircle, LogOut, ChevronRight, Shield, ShieldCheck, ClipboardCheck, Heart } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useBookings, useAddresses, useIdentityVerification, useFavoriteIds } from '../../hooks/useAPI';

interface ProfileScreenProps {
  onNavigate: (screen: any) => void;
  onLogout: () => void;
}

export default function ProfileScreen({ onNavigate, onLogout }: ProfileScreenProps) {
  const { user, signOut } = useAuthStore();
  const { data: bookings, isLoading: bookingsLoading } = useBookings();
  const { data: addresses, isLoading: addressesLoading } = useAddresses();
  const { isVerified, isAdmin } = useIdentityVerification();
  const { data: favIds } = useFavoriteIds();
  const totalFavourites = favIds
    ? ((favIds as any).coach?.length ?? 0)
      + ((favIds as any).court?.length ?? 0)
      + ((favIds as any).club?.length  ?? 0)
    : 0;

  const totalBookings = bookings?.length || 0;
  const savedAddresses = addresses?.length || 0;

  const handleLogout = async () => {
    await signOut();
    onLogout();
  };

  // If not logged in, show login prompt
  if (!user) {
    return (
      <div className="w-full min-h-screen bg-background pb-24 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-6xl mb-4">👤</div>
          <h2 className="text-2xl mb-2">Sign in to view your profile</h2>
          <p className="text-muted-foreground mb-6">
            Access your bookings, saved addresses, and account settings
          </p>
          <button
            onClick={() => onNavigate('login')}
            className="h-12 px-8 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  const menuItems = [
    {
      icon: User,
      label: 'Personal Information',
      subtitle: 'Update your details',
      action: () => onNavigate('personal-info'),
    },
    {
      icon: isVerified ? ShieldCheck : Shield,
      label: 'Identity Verification',
      subtitle: isVerified
        ? 'Verified — AED 500 deposit waived on bookings'
        : 'Verify to waive the AED 500 security deposit',
      action: () => onNavigate('identity-verification'),
    },
    ...(isAdmin ? [{
      icon: ClipboardCheck,
      label: 'Pending Verifications',
      subtitle: 'Admin — review AI-flagged submissions',
      action: () => onNavigate('admin-verifications'),
    }] : []),
    {
      icon: Heart,
      label: 'Favourites',
      subtitle: totalFavourites > 0
        ? `${totalFavourites} saved`
        : 'Save clubs, courts, and coaches you like',
      action: () => onNavigate('favorites'),
    },
    {
      icon: MapPin,
      label: 'Saved Addresses',
      subtitle: addressesLoading ? 'Loading...' : `${savedAddresses} location${savedAddresses !== 1 ? 's' : ''}`,
      action: () => onNavigate('saved-addresses'),
    },
    {
      icon: Bell,
      label: 'Notifications',
      subtitle: 'Manage preferences',
      action: () => onNavigate('notifications'),
    },
    {
      icon: HelpCircle,
      label: 'Help & Support',
      subtitle: 'FAQs, contact us',
      action: () => onNavigate('help-support'),
    },
  ];

  // AppProfile-spec: identity card + 3-col stats + condensed menu list.
  const displayName = (user?.user_metadata?.full_name as string | undefined)?.trim() || 'Player';
  const initials = profileInitials(displayName);
  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    : null;

  return (
    <div className="w-full min-h-screen bg-[#FAFBF7] pb-24">
      <div className="px-5 pt-4 pb-1">
        <div className="font-bold text-2xl tracking-tight text-[#0F1E36]">Profile</div>
      </div>

      {/* Identity card */}
      <div className="mx-5 mt-3 bg-white border border-[#E7E9E2] rounded-2xl p-5 flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-[#0F1E36] text-[#D4F04A] flex items-center justify-center font-bold text-[22px] shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg text-[#0F1E36] truncate">{displayName}</div>
          <div className="text-xs text-[#5F665B] mt-0.5">
            {memberSince ? `Member since ${memberSince}` : (user?.email ?? 'Deuce member')}
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            {isVerified ? (
              <span className="inline-flex items-center gap-1 bg-[#D4F04A] text-[#0F1E36] px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider">
                VERIFIED
              </span>
            ) : (
              <button
                onClick={() => onNavigate('identity-verification')}
                className="inline-flex items-center gap-1 border border-[#D2D6CB] text-[#0F1E36] px-2 py-0.5 rounded-full text-[10px] font-medium hover:border-[#1A6B4F]"
              >
                Verify identity
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 3-col stats */}
      <div className="mx-5 mt-3 grid grid-cols-3 gap-2">
        <Stat n={bookingsLoading ? '…' : String(totalBookings)} l="Bookings" />
        <Stat n={String(totalFavourites)} l="Favourites" />
        <Stat n={addressesLoading ? '…' : String(savedAddresses)} l="Addresses" />
      </div>

      {/* Condensed menu list */}
      <div className="mx-5 mt-5 bg-white border border-[#E7E9E2] rounded-2xl overflow-hidden">
        {menuItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <button
              key={item.label}
              onClick={item.action}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-[#F4F5F2] transition-colors ${
                idx < menuItems.length - 1 ? 'border-b border-[#E7E9E2]' : ''
              }`}
            >
              <Icon className="w-[18px] h-[18px] text-[#1A6B4F]" />
              <span className="flex-1 text-sm text-[#0F1E36]">{item.label}</span>
              {item.subtitle && (
                <span className="text-xs text-[#5F665B] truncate max-w-[40%]">{item.subtitle}</span>
              )}
              <ChevronRight className="w-4 h-4 text-[#8B9183] shrink-0" />
            </button>
          );
        })}
      </div>

      {/* Logout — separated, secondary destructive */}
      <div className="px-5 mt-5">
        <button
          onClick={handleLogout}
          className="w-full bg-white border border-[#E7E9E2] rounded-2xl px-4 py-3.5 flex items-center gap-3 hover:bg-destructive/5 hover:border-destructive/30 transition-colors"
        >
          <LogOut className="w-[18px] h-[18px] text-destructive" />
          <span className="text-sm text-destructive font-medium">Log out</span>
        </button>
      </div>

      <div className="px-6 mt-6 text-center text-xs text-[#8B9183]">
        <p>Deuce v1.0.0 · Tennis. In one place.</p>
        <p className="mt-1">© 2026 Deuce. All rights reserved.</p>
      </div>
    </div>
  );
}

function Stat({ n, l }: { n: string; l: string }) {
  return (
    <div className="bg-white border border-[#E7E9E2] rounded-xl px-3 py-3.5 text-center">
      <div className="font-bold text-[22px] text-[#0F1E36] leading-none">{n}</div>
      <div className="text-[11px] text-[#5F665B] mt-1">{l}</div>
    </div>
  );
}

function profileInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
